import dayjs from "dayjs";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { useRateVariation, useTodayExchangeRate } from "~/hooks/exchangeRates";

export function ExchangeRateWidget() {
  const { data: todayRate, isLoading } = useTodayExchangeRate();

  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  const today = dayjs().format("YYYY-MM-DD");

  const { data: variation } = useRateVariation(yesterday, today);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Tipo de Cambio USD/CLP
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-32" />
          <Skeleton className="mt-2 h-4 w-24" />
        </CardContent>
      </Card>
    );
  }

  if (!todayRate) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Tipo de Cambio USD/CLP
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No hay datos disponibles</p>
        </CardContent>
      </Card>
    );
  }

  const renderVariation = () => {
    if (!variation) return null;

    const { change, changePercent } = variation;
    const isPositive = change > 0;
    const isNegative = change < 0;

    return (
      <div className="flex items-center gap-1 text-sm">
        {isPositive && (
          <>
            <ArrowUp className="h-4 w-4 text-red-500" />
            <span className="text-red-500">
              +{formatCurrency(change)} (+{changePercent.toFixed(2)}%)
            </span>
          </>
        )}
        {isNegative && (
          <>
            <ArrowDown className="h-4 w-4 text-green-500" />
            <span className="text-green-500">
              {formatCurrency(change)} ({changePercent.toFixed(2)}%)
            </span>
          </>
        )}
        {!isPositive && !isNegative && (
          <>
            <Minus className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Sin cambio</span>
          </>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          Tipo de Cambio USD/CLP
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold">
            ${formatCurrency(todayRate.usd_clp)}
          </span>
          <span className="text-muted-foreground">CLP</span>
        </div>
        <div className="mt-1">{renderVariation()}</div>
        <div className="mt-3 text-xs text-muted-foreground">
          <span>{dayjs(todayRate.rate_date).format("DD/MM/YYYY")}</span>
        </div>
      </CardContent>
    </Card>
  );
}
